/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import numeral from 'numeral';
import { padding } from 'polished';
import { get, sumBy } from 'lodash/fp';
import styled from '@emotion/styled';
import { parse as parseQuery } from 'qs';
import { withRouter } from 'react-router-dom';

import Loader from 'app/components/commons/Loader';
import bonusBalanceSelector from 'app/selectors/bonusBalanceSelector';
import ticketBonusSelector from 'app/selectors/ticketBonusSelector';
import t from 'app/utils/translate';
import emailIcon from 'app/images/ui/icon-email.svg';
import { hover } from 'app/utils/cssUtils';
import FormValidationMessage from 'app/components/commons/FormValidationMessage';
import * as userActions from 'app/actions/userActions';
import MessageModal from 'app/components/user/MessageModal';
import { getCostSplit } from 'app/utils/priceUtils';
import CurrencyIcon from 'app/components/commons/CurrencyIcon';
import ChallengeConfirmationModal from './ChallengeConfirmationModal';
import ChallengeEnteredModal from './ChallengeEnteredModal';
import ChallengeShareModal from './ChallengeShareModal';

type Props = {
  // balance?: number,
  bonusBalance?: number,
  cost?: number,
  bonusFreeCost?: number,
  emailVerified: boolean,
  entryIdsToUpdate?: Array<string>,
  entryId?: string,
  showSubmitSuccess?: boolean,
  history: Object,
  isChallengeBuilder?: boolean,
  pxc?: Object,
  isSignedIn?: boolean,
  isSubmitting?: boolean,
  isUpdating?: boolean,
  editLineup?: boolean,
  location: Object,
  mobileVerified: boolean,
  multiEntry?: boolean,
  mustVerifyPhoneToEnterPaidChallenge: boolean,
  mustVerifyEmailToEnterFreeChallenge: boolean,
  onCancel: () => void,
  onSubmit: () => void,
  onUpdate?: () => void,
  requestEmailVerification: () => void,
  requestingEmailVerification: boolean,
  requestingEmailVerificationErrors: Array<Object>,
  submitMore?: boolean,
  showEntryConfirmation?: boolean,
  showUpdateConfirmation?: boolean,
  ticketBonuses?: Array<*>
};

type State = {
  submittingIsSlow: boolean
};

const Stack = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center'
});

const EmailIcon = styled(({ className }) => <img src={emailIcon} alt="" className={className} />)({
  width: 28,
  height: 28
});

const HyperLink = styled.span({
  fontStyle: 'italic',
  textDecoration: 'underline'
});

const DimText = styled.span(({ theme }) => ({
  fontSize: theme.fontSize.small,
  ...padding(theme.spacing.tiny, theme.spacing.small),
  color: theme.color.basis3
}));

const CostDisplayTextContainer = styled.span(({ theme }) => ({
  margin: theme.spacing.xsmall,
  ...padding(theme.spacing.tiny, theme.spacing.small),
  backgroundColor: theme.color.basis6,
  borderRadius: 6,
  color: '#000000'
}));

const BlackTextContainer = styled.div`
  color: #000000;
`;

const Flex = styled.div({
  display: 'flex'
});

const BoldText = styled.span(({ theme }) => ({
  color: theme.color.basis1,
  fontWeight: theme.fontWeight.demiBold,
  fontSize: theme.fontSize.large,
  display: 'block'
}));

const DarkText = styled.span(({ theme }) => ({
  color: theme.color.basis1,
  fontSize: theme.fontSize.medium,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}));

const HyperLinkButton = styled.button(
  {
    display: 'flex',
    transition: 'color 0.2s'
  },
  ({ theme }) => ({
    ...hover({
      color: theme.color.primary
    })
  })
);

const Hr = styled(({ className }) => <hr className={`${className} hr__dark`} />)({
  width: '100%'
});

const Bold = styled.span(({ theme }) => ({
  fontWeight: theme.fontWeight.demiBold
}));

const CostDisplay = styled.div(
  {
    display: 'flex',
    justifyContent: 'center',
    margin: '0 auto'
  },
  ({ theme }) => ({
    fontSize: theme.fontSize.default,
    color: theme.color.basis5
  })
);

const FinePrint = styled.span(({ theme }) => ({
  fontSize: theme.fontSize.tiny,
  color: theme.color.basis4
}));

const Icon = styled.img({
  height: 25,
  width: 25
});

const TicketNum = styled.div({
  '&::after': {
    content: '"\u00a0"'
  }
});

const Divider = styled.div({
  margin: '0 10px',
  fontWeight: 200,
  '&::before': {
    content: '" | "'
  }
});

class OpenChallengeModal extends React.Component<Props, State> {
  props: Props;

  state: State = {
    submittingIsSlow: false
  };

  submitTimeout: number; // eslint-disable-line react/sort-comp

  static defaultProps = {
    // balance: undefined,
    bonusBalance: undefined,
    bonusFreeCost: 0,
    cost: 0,
    entryIdsToUpdate: undefined,
    entryId: undefined,
    editLineup: undefined,
    showSubmitSuccess: false,
    isChallengeBuilder: false,
    pxc: { isEnabled: false },
    isSignedIn: false,
    isSubmitting: false,
    isUpdating: false,
    multiEntry: false,
    submitMore: false,
    showEntryConfirmation: false,
    showUpdateConfirmation: false,
    ticketBonuses: [],
    onUpdate: undefined
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    // if we just started submitting our line up, start a time out
    // if it takes too long, let them know
    const { isUpdating, isSubmitting } = this.props;

    if ((!isUpdating && nextProps.isUpdating) || (!isSubmitting && nextProps.isUpdating)) {
      this.submitTimeout = window.setTimeout(() => {
        this.setState({ submittingIsSlow: true });
      }, 15000);
    }

    // if we're done submitting our line up, remove the slow (incase they do again)
    // and cancel any timeouts going
    if ((isUpdating && !nextProps.isUpdating) || (isSubmitting && !nextProps.isSubmitting)) {
      this.setState({ submittingIsSlow: false });
      clearTimeout(this.submitTimeout);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.submitTimeout);
  }

  handleInviteCancel = () => {
    const { history } = this.props;
    history.replace({ search: '' });
  };

  getTitle = () => {
    const { isSubmitting, editLineup } = this.props;
    if (editLineup) {
      return t('Update Lineup');
    }
    return isSubmitting ? t('Entry Confirmation') : t('Update Entry');
  };

  render() {
    const {
      // balance,
      bonusBalance,
      bonusFreeCost,
      cost = 0,
      emailVerified,
      entryIdsToUpdate,
      entryId,
      isChallengeBuilder,
      editLineup,
      pxc,
      isSignedIn,
      isSubmitting,
      isUpdating,
      location,
      mobileVerified,
      multiEntry,
      mustVerifyPhoneToEnterPaidChallenge,
      mustVerifyEmailToEnterFreeChallenge,
      onCancel,
      onSubmit,
      onUpdate,
      requestEmailVerification,
      requestingEmailVerification,
      requestingEmailVerificationErrors,
      showSubmitSuccess,
      showEntryConfirmation,
      showUpdateConfirmation,
      submitMore,
      ticketBonuses
    } = this.props;

    const { submittingIsSlow } = this.state;

    const query = parseQuery(location.search, { ignoreQueryPrefix: true });

    if (!isSignedIn) {
      return null;
    }

    const balance =
      (window && window.portal && window.portal.user && window.portal.user.account_balance()) || 0;

    const { bonus, primary } = getCostSplit(cost, balance, bonusBalance, bonusFreeCost);

    if (isSubmitting || isUpdating) {
      return (
        <ChallengeConfirmationModal
          title={this.getTitle()}
          isLineup={editLineup}
          onClose={onCancel}
          onConfirm={onUpdate}
          isLoading
          submittingIsSlow={submittingIsSlow}
        />
      );
    }

    if (showEntryConfirmation) {
      const usableBonusBalance = Math.max(0, bonusBalance - bonusFreeCost);

      if (cost > balance + usableBonusBalance && !pxc.isEnabled) {
        return (
          <MessageModal
            message={t('You have insufficient funds to enter this challenge.')}
            cancelText={t('Close')}
            onCancel={onCancel}
            isOpen
          />
        );
      }

      const nonTicketBonusCost = bonus - sumBy('value')(ticketBonuses);

      return (
        <ChallengeConfirmationModal
          title={editLineup ? t('Update Lineup') : t('Entry Confirmation')}
          isLineup={editLineup}
          onClose={onCancel}
          onConfirm={onSubmit}
          topMessageRender={() =>
            cost === 0 &&
            !emailVerified &&
            mustVerifyEmailToEnterFreeChallenge && (
              <Stack>
                <EmailIcon />
                <div>IMPORTANT!</div>
                <div>
                  In the interests of keeping our platform safe and secure you cannot enter a{' '}
                  {t('challenge')} without a<Bold> verified email address</Bold>.
                </div>
                {requestingEmailVerification ? (
                  <Loader kind="dark" />
                ) : (
                  <div>
                    {requestingEmailVerificationErrors.length > 0 && (
                      <FormValidationMessage message={requestingEmailVerificationErrors} />
                    )}
                    <HyperLinkButton onClick={requestEmailVerification}>
                      <HyperLink>
                        <span className="ion-paper-airplane" />
                        Send Verification email
                      </HyperLink>
                    </HyperLinkButton>
                  </div>
                )}
                <Hr />
              </Stack>
            )
          }
        >
          <BlackTextContainer>
            {!editLineup && (
              <span>
                Entry into {multiEntry ? t('these challenges') : t('this challenge')}{' '}
                {cost === 0 ? (
                  'is FREE!'
                ) : (
                  <span>
                    incurs a fee of
                    <BoldText>
                      <CurrencyIcon />
                      {numeral(cost).format('0,0.[00]')}
                    </BoldText>
                  </span>
                )}
              </span>
            )}
            {bonus > 0 && (
              <CostDisplay>
                <CostDisplayTextContainer>
                  <FinePrint>Funds will be taken from the following wallets:</FinePrint>
                  <Flex>
                    <DimText>
                      Bonus wallet{' '}
                      <DarkText>
                        {ticketBonuses.length > 0 && (
                          <React.Fragment>
                            <TicketNum>{`${ticketBonuses.length}x`}</TicketNum>
                            <Icon src={ticketBonuses[0].icon} />
                          </React.Fragment>
                        )}
                        {ticketBonuses.length > 0 && nonTicketBonusCost > 0 && <Divider />}
                        {nonTicketBonusCost > 0 && (!pxc || !pxc.isEnabled) && <CurrencyIcon />}
                        {nonTicketBonusCost > 0 && numeral(nonTicketBonusCost).format('0,0.[00]')}
                      </DarkText>
                    </DimText>
                    <DimText>
                      Primary wallet{' '}
                      <DarkText>
                        <CurrencyIcon />
                        {numeral(pxc && pxc.isEnabled ? cost - bonus : primary).format('0,0.[00]')}
                      </DarkText>
                    </DimText>
                  </Flex>
                </CostDisplayTextContainer>
              </CostDisplay>
            )}
            <br />
            Would you like to proceed?
          </BlackTextContainer>
        </ChallengeConfirmationModal>
      );
    }

    if (isChallengeBuilder) return null;

    if (showUpdateConfirmation) {
      return (
        <ChallengeConfirmationModal
          title={t('Update Entry')}
          isLineup={editLineup}
          onClose={onCancel}
          onConfirm={onUpdate}
        >
          <BlackTextContainer>
            <div>{t('You have already entered into this challenge!')}</div>
            <div>
              {`Would you like to replace your submission with your new selection${
                entryIdsToUpdate.length > 1 ? ` over ${entryIdsToUpdate.length} contests` : ''
              }?`}
            </div>
          </BlackTextContainer>
        </ChallengeConfirmationModal>
      );
    }

    if (showSubmitSuccess) {
      return (
        <ChallengeEnteredModal onClose={onCancel} submitMore={submitMore}>
          <BlackTextContainer>
            {t('Your lineup has been successfully submitted!')}
            <br />
            {t('Follow your score on the Live Leaderboard once the challenge starts.')}
          </BlackTextContainer>
          <FinePrint>Your ticket #{entryId}</FinePrint>
        </ChallengeEnteredModal>
      );
    }

    if (query.invite) {
      return (
        <ChallengeShareModal title={t('Share')} onClose={this.handleInviteCancel}>
          <BlackTextContainer>{t('Invite your friends into the challenge')}</BlackTextContainer>
        </ChallengeShareModal>
      );
    }

    return null;
  }
}

function mapStateToProps(state) {
  return {
    // balance: get('user.currentUser.balance', state),
    bonusBalance: bonusBalanceSelector(state),
    ticketBonuses: ticketBonusSelector(state),
    emailVerified: !!state.user.currentUser.email_verified,
    entryId: get('entry.myEntry.id', state),
    isSignedIn: state.user.isSignedIn,
    mobileVerified: !!state.user.currentUser.phone_verified,
    mustVerifyPhoneToEnterPaidChallenge: state.settings.mustVerifyPhoneToEnterPaidChallenge,
    mustVerifyEmailToEnterFreeChallenge: state.settings.mustVerifyEmailToEnterFreeChallenge,
    requestingEmailVerification: state.user.requestingEmailVerification,
    requestingEmailVerificationErrors: state.user.requestingEmailVerificationErrors
  };
}

const actions = {
  requestEmailVerification: userActions.requestEmailVerification
};

function mergeProps(stateProps, dispatchProps, ownProps) {
  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    entryId: ownProps.entryId || stateProps.entryId
  };
}

export default withRouter(connect(mapStateToProps, actions, mergeProps)(OpenChallengeModal));
